import { useCallback, useEffect, useState } from 'react'
import { LIMITS } from '@weblite/permission'
import { useCountDownToDate } from '@weblite/logic-toolkit'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import {
  resendCode,
  VerificationSession,
  VerifyError,
  verifyPhone,
} from '$logic/auth'
import {
  clearAttempts,
  getBlockedUntil,
  recordAttempt,
} from '$logic/auth/resend-block-storage'
import { t } from '$i18n'
import { nothing } from '$func/utils'

const ERROR_MAPPER: Record<VerifyError, string> = {
  WRONG_CODE: t('signin.verification.wrongCodeEntered'),
  SERVER_ERROR: t('serverError'),
  CLOSE_VERIFICATION: '',
}

const calculateEndDate = () =>
  LIMITS.verificationCode.resendInSeconds * 1000 + dateWithOffset()

export function useVerifyPhoneForm({
  phone,
  sessionId,
  signInUser,
  onVerified,
  onCloseVerification = nothing,
}: VerificationSession & {
  signInUser?: boolean
  onVerified?: (token: string) => void
  onCloseVerification?: () => unknown
}) {
  const [loading, setLoading] = useState(false)
  const [alertText, setAlertText] = useState('')

  const [endDate, setEndDate] = useState(calculateEndDate())
  const [blockedUntil, setBlockedUntil] = useState<number | null>(() =>
    getBlockedUntil(phone),
  )

  const remainingMillis = useCountDownToDate(endDate, dateWithOffset)
  const blockRemainingMillis = useCountDownToDate(
    blockedUntil ?? 0,
    dateWithOffset,
  )

  const resendBlocked = blockedUntil != null && blockRemainingMillis > 0

  useEffect(() => {
    if (blockedUntil != null && blockRemainingMillis <= 0) {
      clearAttempts(phone)
      setBlockedUntil(null)
    }
  }, [phone, blockedUntil, blockRemainingMillis])

  const handleResendCode = useCallback(async () => {
    if (resendBlocked) return
    setLoading(true)
    try {
      const result = await resendCode(phone, sessionId)
      if (result === 'OK') {
        setEndDate(calculateEndDate())
        const { blockedUntil: newBlock } = recordAttempt(phone)
        if (newBlock) setBlockedUntil(newBlock)
      } else {
        setAlertText(t('signin.verification.resendFailed'))
      }
    } finally {
      setLoading(false)
    }
  }, [phone, sessionId, resendBlocked])

  const handleCodeComplete = useCallback(
    async (code: string) => {
      setLoading(true)
      try {
        const res = await verifyPhone(phone, sessionId, code, signInUser)
        if (typeof res !== 'string') {
          clearAttempts(phone)
          onVerified?.(res.token)
        } else if (res === 'CLOSE_VERIFICATION') onCloseVerification()
        else setAlertText(ERROR_MAPPER[res])
      } finally {
        setLoading(false)
      }
    },
    [phone, sessionId, onVerified, signInUser, onCloseVerification],
  )

  const handleCodeChange = useCallback(
    async (code: string) => {
      if (code.length === LIMITS.verificationCode.length) {
        await handleCodeComplete(code)
      } else setAlertText('')
    },
    [handleCodeComplete],
  )

  return {
    handleCodeChange,
    handleResendCode,
    resendBlocked,
    blockRemainingMillis,
    remainingMillis,
    alertText,
    loading,
  }
}
